import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, 
  UserPlus,
  UserMinus,
  Check,
  X,
  MessageSquare,
  ArrowLeft,
  Clock,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyConnections = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [removeDialog, setRemoveDialog] = useState({ open: false, connection: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const [connRes, inRes, outRes] = await Promise.all([
        axios.get(`${API}/community/connections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/community/connections/requests/incoming`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/community/connections/requests/outgoing`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConnections(connRes.data.connections || []);
      setIncomingRequests(inRes.data.requests || []);
      setOutgoingRequests(outRes.data.requests || []);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    setActionLoading(requestId);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/connections/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? '¡Conexión aceptada!' : 'Connection accepted!');
      fetchData();
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (requestId) => {
    setActionLoading(requestId);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/connections/requests/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Solicitud rechazada' : 'Request declined');
      fetchData();
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async () => {
    if (!removeDialog.connection) return;
    setActionLoading(removeDialog.connection.id);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.delete(`${API}/community/connections/${removeDialog.connection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Conexión eliminada' : 'Connection removed');
      setRemoveDialog({ open: false, connection: null });
      fetchData();
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="connections-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-12 w-full" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-connections">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/community')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {language === 'es' ? 'Mis Conexiones' : 'My Connections'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Gestiona tus conexiones y solicitudes'
              : 'Manage your connections and requests'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="connections" className="space-y-4">
        <TabsList>
          <TabsTrigger value="connections" data-testid="tab-connections">
            <Users className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Conexiones' : 'Connections'} ({connections.length})
          </TabsTrigger>
          <TabsTrigger value="incoming" data-testid="tab-incoming">
            <UserPlus className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Recibidas' : 'Incoming'} ({incomingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="outgoing" data-testid="tab-outgoing">
            <Clock className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Enviadas' : 'Sent'} ({outgoingRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-4">
          {connections.length > 0 ? (
            connections.map((conn, index) => (
              <Card key={conn.id} data-testid={`connection-${index}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">
                          {conn.connected_profile?.display_name || (language === 'es' ? 'Defensor' : 'Advocate')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {conn.connected_profile?.location_display || ''}
                        </p>
                        {conn.connected_profile?.issue_areas && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {conn.connected_profile.issue_areas.slice(0, 2).map((issue, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {issue.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/community/messages?to=${conn.connected_profile?.id}`)}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        {language === 'es' ? 'Mensaje' : 'Message'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => setRemoveDialog({ open: true, connection: conn })}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-medium text-lg mb-2">
                  {language === 'es' ? 'Sin conexiones aún' : 'No connections yet'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'es' 
                    ? 'Descubre defensores para comenzar a conectar'
                    : 'Discover advocates to start connecting'}
                </p>
                <Button onClick={() => navigate('/community/discover')}>
                  {language === 'es' ? 'Descubrir Defensores' : 'Discover Advocates'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Incoming Requests Tab */}
        <TabsContent value="incoming" className="space-y-4">
          {incomingRequests.length > 0 ? (
            incomingRequests.map((req, index) => (
              <Card key={req.id} className="border-primary/30 bg-primary/5" data-testid={`incoming-${index}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <UserPlus className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">
                          {req.from_profile?.display_name || (language === 'es' ? 'Defensor' : 'Advocate')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {req.from_profile?.location_display || ''}
                        </p>
                        <p className="text-sm mt-2 p-2 bg-background rounded">
                          "{req.message}"
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button 
                        size="sm"
                        onClick={() => handleAccept(req.id)}
                        disabled={actionLoading === req.id}
                      >
                        {actionLoading === req.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        {language === 'es' ? 'Aceptar' : 'Accept'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDecline(req.id)}
                        disabled={actionLoading === req.id}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <UserPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {language === 'es' ? 'No hay solicitudes pendientes' : 'No pending requests'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Outgoing Requests Tab */}
        <TabsContent value="outgoing" className="space-y-4">
          {outgoingRequests.length > 0 ? (
            outgoingRequests.map((req, index) => (
              <Card key={req.id} data-testid={`outgoing-${index}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Clock className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'es' ? 'Solicitud enviada' : 'Request sent'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {language === 'es' ? 'Pendiente' : 'Pending'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {language === 'es' ? 'No hay solicitudes enviadas' : 'No sent requests'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Remove Connection Dialog */}
      <Dialog open={removeDialog.open} onOpenChange={(open) => setRemoveDialog({ open, connection: removeDialog.connection })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? '¿Eliminar conexión?' : 'Remove connection?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Esta acción no se puede deshacer. Ya no podrán enviarse mensajes.'
                : 'This action cannot be undone. You will no longer be able to message each other.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialog({ open: false, connection: null })}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleRemove} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {language === 'es' ? 'Eliminar' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyConnections;
